// src/utils/controlChoiceUrl.ts
//
// An OPEN control's choice lives in the URL, per control (ADR-0350 decision
// 7): `?c.<block_key>=Email&c.<block_key>=Paid%20Search`. Keyed by the
// control's block key, not its field, because two controls may steer
// different tiles on one field. One parameter per VALUE, read with `getAll`,
// as the filter bar does (`barFilterUrl.ts`): campaign names contain commas.
//
// - No parameter means the control's DEFAULT (empty default = no filter).
// - One empty parameter (`?c.<key>=`) means the viewer cleared a control that
//   has a default; without it a cleared control would snap back on reload.
//   On a REQUIRED control (plan 2026-10-09 button slicer) there is no
//   cleared state: the parameter resolves to the default and leaves the URL.
// - A link with more values than the control's maximum selections keeps
//   the first N.
// - A parameter for a block key that is not an open control on this
//   dashboard (deleted, or a hand-made link) leaves the URL silently: the
//   control is gone, there is nothing to tell the viewer about.
//
// The register control (`?dim.attribution_model=`, `utils/controlUrl.ts`) and
// the filter bar (`?f.<key>=`) keep their own namespaces. Pure, so the rules
// are testable without mounting Insights.
import { MAX_BAR_VALUES, MAX_BAR_VALUE_LENGTH } from "@/components/dashboards/filters/barFilterUrl";

export const CONTROL_PARAM_PREFIX = "c.";

export type ControlChoiceSpec = {
  selection: "single" | "multi";
  /** The author's default, already capped by the backend's shape check. */
  default: readonly string[];
  /** The control always holds a value (`controlSettings(...).required`). A
   *  UX rule, not an access rule: the refresh endpoint does not enforce it. */
  required?: boolean;
  /** At most this many values (multi only); null/absent = no cap. */
  maxSelections?: number | null;
};

export type ControlChoiceResolution = {
  /** Block key -> the values the control applies now (its default when the
   *  URL says nothing; empty = no filter). Every control in `controls`. */
  choices: Record<string, string[]>;
  /** Parameter names that must leave the URL. */
  strip: string[];
};

function sameValues(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((v) => set.has(v));
}

/** Each open control's current choice, from the URL and the defaults.
 *  `controls` maps block key -> the control's stored selection and default. */
export function resolveControlChoiceParams(
  params: URLSearchParams,
  controls: ReadonlyMap<string, ControlChoiceSpec>,
  /** Block keys of the date range controls: their `c.` value is a period
   *  (`utils/dateControlPeriods.ts`), read there, and stays in the URL. */
  periodControls: ReadonlySet<string> = new Set(),
): ControlChoiceResolution {
  const choices: Record<string, string[]> = {};
  const strip: string[] = [];
  for (const [blockKey, spec] of controls) choices[blockKey] = [...spec.default];
  for (const name of new Set(params.keys())) {
    if (!name.startsWith(CONTROL_PARAM_PREFIX)) continue;
    if (periodControls.has(name.slice(CONTROL_PARAM_PREFIX.length))) continue;
    const spec = controls.get(name.slice(CONTROL_PARAM_PREFIX.length));
    if (!spec) {
      strip.push(name);
      continue;
    }
    const raw = params.getAll(name);
    let values = Array.from(new Set(raw.filter((v) => v.length > 0 && v.length <= MAX_BAR_VALUE_LENGTH)));
    const cap = spec.selection === "single" ? 1 : Math.min(MAX_BAR_VALUES, spec.maxSelections ?? MAX_BAR_VALUES);
    values = values.slice(0, cap);
    const cleared = raw.every((v) => v === "");
    if (!values.length && !(cleared && spec.default.length && !spec.required)) {
      // Nothing usable, an empty parameter on a control without a default
      // (which already means "no filter"), or one on a required control
      // (which cannot be cleared): normalise it away, the default applies.
      strip.push(name);
      continue;
    }
    if (values.length && sameValues(values, spec.default)) {
      strip.push(name);
      continue;
    }
    choices[name.slice(CONTROL_PARAM_PREFIX.length)] = values;
  }
  return { choices, strip };
}

/** The URL after the viewer set the control `blockKey` to `values`. Its
 *  default (or nothing, when it has none) removes the parameter; clearing a
 *  control that has a default writes the one empty parameter. */
export function withControlChoiceParam(
  prev: URLSearchParams,
  blockKey: string,
  values: readonly string[],
  defaultValues: readonly string[],
): URLSearchParams {
  const next = new URLSearchParams(prev);
  const name = `${CONTROL_PARAM_PREFIX}${blockKey}`;
  next.delete(name);
  if (sameValues(values, defaultValues)) return next;
  if (!values.length) {
    next.append(name, "");
    return next;
  }
  for (const value of values) next.append(name, value);
  return next;
}

/** The URL with every control choice removed: each control back to its
 *  default (the "Reset filters" button). With `blockKeys`, only those
 *  controls' choices (one page's). */
export function withoutControlChoiceParams(
  prev: URLSearchParams,
  blockKeys?: Iterable<string>,
): URLSearchParams {
  const only = blockKeys ? new Set(blockKeys) : null;
  const next = new URLSearchParams(prev);
  for (const name of new Set(prev.keys())) {
    if (!name.startsWith(CONTROL_PARAM_PREFIX)) continue;
    if (only && !only.has(name.slice(CONTROL_PARAM_PREFIX.length))) continue;
    next.delete(name);
  }
  return next;
}
